<div
  class="eventq-datetime-picker"
  [class.eventq-datetime-picker--field]="mode() === 'field'"
  [class.eventq-datetime-picker--open]="panelOpen()"
>
  @if (mode() === 'field') {
    <button
      type="button"
      class="eventq-datetime-picker__field"
      [disabled]="disabled()"
      [attr.aria-expanded]="panelOpen()"
      [attr.aria-label]="fieldLabel()"
      [attr.aria-required]="required() ? true : null"
      (click)="onTogglePanel()"
    >
      <span class="eventq-datetime-picker__field-label-notch">
        <span class="eventq-datetime-picker__field-label">{{ fieldLabel() }}</span>
        @if (required()) {
          <span class="eventq-datetime-picker__field-required" aria-hidden="true">*</span>
        }
      </span>
      <span
        class="eventq-datetime-picker__field-value"
        [class.eventq-datetime-picker__field-value--placeholder]="!hasValue()"
      >
        {{ fieldDisplay() }}
      </span>
      <mat-icon class="eventq-datetime-picker__field-icon" aria-hidden="true">calendar_today</mat-icon>
    </button>
  }

  @if (mode() === 'inline' || panelOpen()) {
    <div
      #panel
      class="eventq-datetime-picker__panel"
      [class.eventq-datetime-picker__panel--popover]="mode() === 'field'"
      [class.eventq-datetime-picker__panel--below]="mode() === 'field' && resolvedPlacement() === 'below'"
      [class.eventq-datetime-picker__panel--above]="mode() === 'field' && resolvedPlacement() === 'above'"
      [class.eventq-datetime-picker__panel--align-start]="mode() === 'field' && panelAlign() === 'start'"
      [class.eventq-datetime-picker__panel--align-end]="mode() === 'field' && panelAlign() === 'end'"
      [style.position]="panelStyle().position"
      [style.top]="panelStyle().top"
      [style.bottom]="panelStyle().bottom"
      [style.left]="panelStyle().left"
      [style.right]="panelStyle().right"
      [style.width]="panelStyle().width"
      [style.z-index]="panelStyle().zIndex"
      role="dialog"
      [attr.aria-label]="dateOnly() ? 'Date picker' : 'Date and time picker'"
    >
      <div class="eventq-datetime-picker__calendar">
        <div class="eventq-datetime-picker__month-header">
          <button
            type="button"
            class="eventq-datetime-picker__nav-btn"
            aria-label="Previous month"
            [disabled]="!canGoPreviousMonth()"
            (click)="onPreviousMonth()"
          >
            <mat-icon aria-hidden="true">chevron_left</mat-icon>
          </button>
          <span class="eventq-datetime-picker__month-label">{{ monthLabel() }}</span>
          <button
            type="button"
            class="eventq-datetime-picker__nav-btn"
            aria-label="Next month"
            [disabled]="!canGoNextMonth()"
            (click)="onNextMonth()"
          >
            <mat-icon aria-hidden="true">chevron_right</mat-icon>
          </button>
        </div>

        <div class="eventq-datetime-picker__week-header">
          @for (label of weekdayLabels; track label) {
            <span class="eventq-datetime-picker__weekday">{{ label }}</span>
          }
        </div>

        @for (week of calendarWeeks(); track $index) {
          <div class="eventq-datetime-picker__week">
            @for (cell of week; track cell.date.getTime()) {
              <button
                type="button"
                class="eventq-datetime-picker__day"
                [class.eventq-datetime-picker__day--outside]="!cell.inMonth"
                [class.eventq-datetime-picker__day--selected]="cell.isSelected"
                [class.eventq-datetime-picker__day--today]="cell.isToday"
                [class.eventq-datetime-picker__day--disabled]="cell.disabled"
                [disabled]="cell.disabled"
                (click)="onSelectDay(cell.date, cell.disabled)"
              >
                {{ cell.day }}
              </button>
            }
          </div>
        }
      </div>

      @if (!dateOnly()) {
      <div class="eventq-datetime-picker__time">
        <div class="eventq-datetime-picker__time-segment">
          <input
            class="eventq-datetime-picker__time-value"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            aria-label="Hours"
            [disabled]="disabled()"
            [value]="hoursField()"
            (focus)="onTimeFocus('hours')"
            (keydown)="onTimeKeydown('hours', $event)"
            (paste)="onTimePaste('hours', $event)"
            (blur)="onTimeBlur('hours')"
          />
          <div class="eventq-datetime-picker__time-stepper">
            <button
              type="button"
              aria-label="Increase hours"
              [disabled]="disabled()"
              (click)="onStepHours(1, $event)"
            >
              <mat-icon aria-hidden="true">keyboard_arrow_up</mat-icon>
            </button>
            <button
              type="button"
              aria-label="Decrease hours"
              [disabled]="disabled()"
              (click)="onStepHours(-1, $event)"
            >
              <mat-icon aria-hidden="true">keyboard_arrow_down</mat-icon>
            </button>
          </div>
        </div>
        <span class="eventq-datetime-picker__time-colon">:</span>
        <div class="eventq-datetime-picker__time-segment">
          <input
            class="eventq-datetime-picker__time-value"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            aria-label="Minutes"
            [disabled]="disabled()"
            [value]="minutesField()"
            (focus)="onTimeFocus('minutes')"
            (keydown)="onTimeKeydown('minutes', $event)"
            (paste)="onTimePaste('minutes', $event)"
            (blur)="onTimeBlur('minutes')"
          />
          <div class="eventq-datetime-picker__time-stepper">
            <button
              type="button"
              aria-label="Increase minutes"
              [disabled]="disabled()"
              (click)="onStepMinutes(1, $event)"
            >
              <mat-icon aria-hidden="true">keyboard_arrow_up</mat-icon>
            </button>
            <button
              type="button"
              aria-label="Decrease minutes"
              [disabled]="disabled()"
              (click)="onStepMinutes(-1, $event)"
            >
              <mat-icon aria-hidden="true">keyboard_arrow_down</mat-icon>
            </button>
          </div>
        </div>
        @if (showSeconds()) {
          <span class="eventq-datetime-picker__time-colon">:</span>
          <div class="eventq-datetime-picker__time-segment">
            <input
              class="eventq-datetime-picker__time-value"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              aria-label="Seconds"
              [disabled]="disabled()"
              [value]="secondsField()"
              (focus)="onTimeFocus('seconds')"
              (keydown)="onTimeKeydown('seconds', $event)"
              (paste)="onTimePaste('seconds', $event)"
              (blur)="onTimeBlur('seconds')"
            />
            <div class="eventq-datetime-picker__time-stepper">
              <button
                type="button"
                aria-label="Increase seconds"
                [disabled]="disabled()"
                (click)="onStepSeconds(1, $event)"
              >
                <mat-icon aria-hidden="true">keyboard_arrow_up</mat-icon>
              </button>
              <button
                type="button"
                aria-label="Decrease seconds"
                [disabled]="disabled()"
                (click)="onStepSeconds(-1, $event)"
              >
                <mat-icon aria-hidden="true">keyboard_arrow_down</mat-icon>
              </button>
            </div>
          </div>
        }
      </div>
      }

      @if (showActions()) {
        <div class="eventq-datetime-picker__actions">
          <hr class="eventq-datetime-picker__divider" />
          <div class="eventq-datetime-picker__buttons">
            <button type="button" class="eventq-datetime-picker__btn eventq-datetime-picker__btn--cancel" (click)="onCancel()">
              Cancel
            </button>
            <button
              type="button"
              class="eventq-datetime-picker__btn eventq-datetime-picker__btn--apply"
              [disabled]="!canApply()"
              (click)="onApply()"
            >
              Apply
            </button>
          </div>
        </div>
      }
    </div>
  }
</div>
